import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import axios from "axios";
import { formatDualCurrency } from "../config/fx";
import { 
  MapPin,
  CheckCircle2,
  ArrowLeft,
  Package,
  Clock,
  Building2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const SupplierProfile = () => {
  const { t } = useTranslation();
  const { supplierId } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSupplier();
    fetchProducts();
  }, [supplierId]);

  const getImageUrl = (paths) => {
    if (!paths || paths.length === 0) return null;
    const path = paths[0];
    if (!path) return null;
    return path.startsWith("http") ? path : `${API_URL}${path}`;
  };

  const fetchSupplier = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suppliers/${supplierId}`);
      setSupplier(response.data);
    } catch (err) {
      console.error('Failed to fetch supplier:', err);
    }
    setLoading(false);
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products?supplier_id=${supplierId}`);
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-48 w-full rounded-md mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 rounded-md" />
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="container-main py-16 text-center">
        <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Supplier not found</h2>
        <Link to="/suppliers">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Suppliers
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="supplier-profile-page">
      {/* Header */}
      <div className="bg-[#0D4F8B] py-12">
        <div className="container-main">
          <Link to="/suppliers" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Suppliers
          </Link>
          
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-white rounded-md flex items-center justify-center text-[#0D4F8B] font-bold text-4xl">
              {supplier.company_name.charAt(0)}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {supplier.company_name}
                </h1>
                {supplier.verified && (
                  <Badge className="bg-[#10B981] hover:bg-[#10B981]">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {t('suppliers.verified')}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <MapPin className="w-4 h-4" />
                {supplier.location}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-main py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div>
            <Card className="card-base">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">About</h3>
                <p className="text-slate-600 text-sm mb-6">
                  {supplier.description}
                </p>
                
                <h4 className="font-medium text-slate-900 mb-2">Categories</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {supplier.categories.map((cat) => (
                    <Badge key={cat} variant="outline">
                      {cat}
                    </Badge>
                  ))}
                </div>
                
                <Link to={`/rfq?supplier=${supplier.id}`}>
                  <Button className="w-full bg-[#10B981] hover:bg-[#059669]" aria-label={`Request quotation from ${supplier.company_name}`}>
                    Request Quotation
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Products */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Products ({products.length})
            </h2>
            
            {products.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No products listed yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {products.map((product) => (
                  <Card key={product.id} className="card-base card-interactive hover-lift">
                      <CardContent className="p-4">
                      <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-200 rounded-md flex items-center justify-center mb-4 overflow-hidden">
                        {getImageUrl(product.image_paths) ? (
                          <img
                            src={getImageUrl(product.image_paths)}
                            alt={product.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <Package className="w-10 h-10 text-slate-300" aria-hidden="true" />
                        )}
                      </div>
                      <Badge variant="outline" className="mb-2 text-xs">
                        {product.category}
                      </Badge>
                      <h3 className="font-semibold text-slate-900 mb-2 line-clamp-1">
                        {product.name}
                      </h3>
                      <div className="text-lg font-bold text-[#0D4F8B] mb-2">
                        {formatDualCurrency(product.price_min_usd).usd}
                      </div>
                      <div className="text-xs text-slate-500 mb-2">
                        {formatDualCurrency(product.price_min_usd).aed}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>MOQ: {product.moq}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {product.lead_time_days}d
                        </span>
                      </div>
                      <Link to={`/marketplace/${product.id}`} className="block mt-3">
                        <Button variant="outline" size="sm" className="w-full">
                          View Details
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierProfile;

